import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { Check, Mail, Type, HelpCircle, MessageSquare, FileText } from 'lucide-react'
import toast from 'react-hot-toast'
import Card, { CardHeader, CardTitle, CardDescription, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import Input from '../UI/Input'
import Select from '../UI/Select'
import Textarea from '../UI/Textarea'
import { HUB_BRANDING_QUERY, hubBrandingAPI } from '../../hooks/useHubBranding'

const EMAIL_BRANDING_DEFAULTS = {
  email_brand_name: 'Enginuo',
  email_support_email: 'support@enginuo.com',
  email_tagline: 'a comprehensive product management platform designed to help teams collaborate and succeed.',
  email_footer: 'Enginuo crafts product management solutions that give businesses in dynamic industries the tools to innovate, collaborate, and thrive.'
}

const EMAIL_TEMPLATES = [
  {
    id: 'hub-invitation',
    label: 'Hub invitation',
    badge: 'Invitation',
    showDescription: true,
    headline: "You're Invited!",
    subhead: (name) => `Join ${name}`,
    greeting: 'Hello,',
    button: 'Accept Invitation',
    body: (name, tagline) => (
      <>
        You have been invited to join <span className="font-semibold text-secondary-900">{name}</span>, {tagline}
      </>
    )
  },
  {
    id: 'org-invitation',
    label: 'Organization invitation',
    badge: 'Invitation',
    headline: 'Invitation to Join',
    subhead: () => 'Acme Organization',
    greeting: 'Hi Sam,',
    button: 'Accept Invitation',
    body: (name, _tagline, support) => (
      <>
        You have been invited to join <span className="font-semibold text-secondary-900">Acme Organization</span> on {name}.
        Need help? Contact {support}.
      </>
    )
  },
  {
    id: 'hub-welcome',
    label: 'Hub welcome',
    badge: 'Welcome',
    headline: (name) => `Welcome to ${name}!`,
    subhead: () => 'Your account has been successfully created',
    greeting: 'Hello Sam!',
    button: 'Login to Your Account',
    body: (name) => (
      <>
        Congratulations! Your account has been successfully created and you&apos;re now a member of{' '}
        <span className="font-semibold text-secondary-900">{name}</span>.
      </>
    )
  },
  {
    id: 'org-welcome',
    label: 'Organization welcome',
    badge: 'Welcome',
    headline: () => 'Welcome to Acme Organization!',
    subhead: () => 'Your account has been successfully created',
    greeting: 'Hello Sam!',
    button: 'Login to Your Account',
    body: (name) => (
      <>
        Your account is ready. You can now sign in and start using {name} with Acme Organization.
      </>
    )
  },
  {
    id: 'password-reset',
    label: 'Password reset',
    badge: 'Password reset',
    headline: 'Reset Your Password',
    subhead: (name) => `${name} password reset request`,
    greeting: 'Hello Sam!',
    button: 'Reset Password',
    body: (name) => (
      <>
        We received a request to reset the password for your {name} account. This link expires in 1 hour.
      </>
    )
  },
  {
    id: 'subscription',
    label: 'Subscription confirmed',
    badge: 'Subscription',
    headline: 'Subscription Confirmed',
    subhead: () => 'Your subscription has been created successfully',
    greeting: 'Hello,',
    button: null,
    body: (name) => (
      <>
        Thank you for subscribing. Your {name} subscription is now active and ready to use.
      </>
    )
  },
  {
    id: 'subscription-expiring',
    label: 'Subscription expiring',
    badge: 'Subscription',
    headline: 'Subscription Expiring Soon',
    subhead: () => 'Action required',
    greeting: 'Hello Acme Organization!',
    button: null,
    body: () => (
      <>
        Your subscription will expire soon. Renew to keep access to premium features. Your data will be preserved for a limited time.
      </>
    )
  },
  {
    id: 'org-created',
    label: 'Organization created',
    badge: 'Organization',
    headline: 'Acme Organization',
    subhead: () => 'Your organization is ready',
    greeting: 'Hi Sam,',
    button: 'Go to organization dashboard',
    body: (name) => (
      <>
        Your organization <span className="font-semibold text-secondary-900">Acme Organization</span> has been created successfully in {name}.
      </>
    )
  },
  {
    id: 'contact-confirmation',
    label: 'Contact confirmation',
    badge: 'Contact',
    headline: 'Thank You for Contacting Us!',
    subhead: () => "We've received your message",
    greeting: 'Hello Sam!',
    button: null,
    body: () => (
      <>
        We have received your inquiry and our team will get back to you as soon as possible.
      </>
    )
  }
]

function resolveText(value, name) {
  return typeof value === 'function' ? value(name) : value
}

function EmailPreview({ templateId, brandName, tagline, footerText, supportEmail, logoUrl }) {
  const name = brandName || EMAIL_BRANDING_DEFAULTS.email_brand_name
  const description = tagline || EMAIL_BRANDING_DEFAULTS.email_tagline
  const footer = footerText || EMAIL_BRANDING_DEFAULTS.email_footer
  const support = supportEmail || EMAIL_BRANDING_DEFAULTS.email_support_email
  const template = EMAIL_TEMPLATES.find((item) => item.id === templateId) || EMAIL_TEMPLATES[0]

  return (
    <div className="overflow-hidden rounded-xl border border-secondary-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-secondary-200 px-4 py-3">
        {logoUrl ? (
          <img src={logoUrl} alt="" className="h-7 w-auto max-w-[120px] object-contain" />
        ) : (
          <p className="text-base font-bold tracking-wide text-secondary-900">{name}</p>
        )}
        <p className="text-[11px] font-semibold uppercase tracking-wider text-secondary-500">{template.badge}</p>
      </div>
      <p className="bg-secondary-50 px-4 py-2 text-center text-[11px] text-secondary-500">
        This is an automated email from {name}. Please do not reply directly to this message.
      </p>
      <div className="px-5 py-6">
        <p className="text-2xl font-bold text-sky-300">{resolveText(template.headline, name)}</p>
        <p className="mt-1 text-xs text-secondary-500">{resolveText(template.subhead, name)}</p>
        <p className="mt-6 text-sm font-semibold text-secondary-900">{template.greeting}</p>
        <p className="mt-3 text-sm leading-relaxed text-secondary-600">
          {template.body(name, description, support)}
        </p>
        {template.button && (
          <div className="mt-5 rounded-md bg-sky-50 px-4 py-3 text-center">
            <span className="inline-block rounded-md bg-sky-800 px-4 py-2 text-xs font-semibold text-white">
              {template.button}
            </span>
          </div>
        )}
        <p className="mt-4 text-xs text-secondary-500">Need help? Contact {support}</p>
      </div>
      <div className="border-t border-secondary-200 px-5 py-4">
        <p className="text-xs leading-relaxed text-secondary-500">{footer}</p>
        <p className="mt-2 text-[11px] text-secondary-400">
          This is an automated message from {name}. Please do not reply to this email.
        </p>
      </div>
    </div>
  )
}

export default function EmailBrandingTab({ branding, active = true }) {
  const queryClient = useQueryClient()
  const [templateId, setTemplateId] = useState('hub-invitation')
  const [brandName, setBrandName] = useState(EMAIL_BRANDING_DEFAULTS.email_brand_name)
  const [supportEmail, setSupportEmail] = useState(EMAIL_BRANDING_DEFAULTS.email_support_email)
  const [tagline, setTagline] = useState(EMAIL_BRANDING_DEFAULTS.email_tagline)
  const [footerText, setFooterText] = useState(EMAIL_BRANDING_DEFAULTS.email_footer)

  useEffect(() => {
    if (!branding) return
    setBrandName(branding.email_brand_name || EMAIL_BRANDING_DEFAULTS.email_brand_name)
    setSupportEmail(branding.email_support_email || EMAIL_BRANDING_DEFAULTS.email_support_email)
    setTagline(branding.email_tagline || EMAIL_BRANDING_DEFAULTS.email_tagline)
    setFooterText(branding.email_footer || EMAIL_BRANDING_DEFAULTS.email_footer)
  }, [branding])

  const selectedTemplate = EMAIL_TEMPLATES.find((item) => item.id === templateId) || EMAIL_TEMPLATES[0]
  const showInvitationDescription = Boolean(selectedTemplate.showDescription)

  const applySavedBranding = () => {
    setBrandName(branding?.email_brand_name || EMAIL_BRANDING_DEFAULTS.email_brand_name)
    setSupportEmail(branding?.email_support_email || EMAIL_BRANDING_DEFAULTS.email_support_email)
    setTagline(branding?.email_tagline || EMAIL_BRANDING_DEFAULTS.email_tagline)
    setFooterText(branding?.email_footer || EMAIL_BRANDING_DEFAULTS.email_footer)
  }

  const isDirty = useMemo(() => {
    if (!branding) return false
    return (
      brandName.trim() !== (branding.email_brand_name || EMAIL_BRANDING_DEFAULTS.email_brand_name) ||
      supportEmail.trim() !== (branding.email_support_email || EMAIL_BRANDING_DEFAULTS.email_support_email) ||
      tagline.trim() !== (branding.email_tagline || EMAIL_BRANDING_DEFAULTS.email_tagline) ||
      footerText.trim() !== (branding.email_footer || EMAIL_BRANDING_DEFAULTS.email_footer)
    )
  }, [brandName, branding, footerText, supportEmail, tagline])

  const mutation = useMutation((formData) => hubBrandingAPI.update(formData), {
    onSuccess: () => {
      queryClient.invalidateQueries([HUB_BRANDING_QUERY])
      toast.success('Email branding saved')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to save email branding')
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!brandName.trim()) {
      toast.error('Email brand name is required')
      return
    }
    if (!supportEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supportEmail.trim())) {
      toast.error('Enter a valid support email')
      return
    }
    if (showInvitationDescription && !tagline.trim()) {
      toast.error('Email description is required')
      return
    }
    if (!footerText.trim()) {
      toast.error('Email footer is required')
      return
    }

    const formData = new FormData()
    formData.append('email_brand_name', brandName.trim())
    formData.append('email_support_email', supportEmail.trim())
    formData.append('email_tagline', tagline.trim())
    formData.append('email_footer', footerText.trim())
    mutation.mutate(formData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 pb-24">
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Mail className="mr-2 h-5 w-5 text-primary-600" />
                Email template
              </CardTitle>
              <CardDescription>
                Choose which email to preview. Brand name, support email, and footer apply to all of them.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <label htmlFor="email_template" className="mb-2 block text-sm font-medium text-secondary-800">
                Template
              </label>
              <Select
                id="email_template"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
              >
                {EMAIL_TEMPLATES.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.label}
                  </option>
                ))}
              </Select>
              <p className="mt-2 text-xs text-secondary-500">
                Previewing <span className="font-medium text-secondary-700">{selectedTemplate.label}</span>. Switching templates does not save a separate copy.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Type className="mr-2 h-5 w-5 text-primary-600" />
                Email brand name
              </CardTitle>
              <CardDescription>
                Shown in the header, body, and footer of every Product Hub email.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <label htmlFor="email_brand_name" className="mb-2 block text-sm font-medium text-secondary-800">
                Brand name
              </label>
              <Input
                id="email_brand_name"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                maxLength={100}
                required
                placeholder="Enginuo"
              />
              <p className="mt-2 text-xs text-secondary-400">{brandName.trim().length}/100</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <HelpCircle className="mr-2 h-5 w-5 text-primary-600" />
                Support email
              </CardTitle>
              <CardDescription>Contact address used in invitation and help links.</CardDescription>
            </CardHeader>
            <CardContent>
              <label htmlFor="email_support_email" className="mb-2 block text-sm font-medium text-secondary-800">
                Support email
              </label>
              <Input
                id="email_support_email"
                type="email"
                value={supportEmail}
                onChange={(e) => setSupportEmail(e.target.value)}
                maxLength={255}
                required
                placeholder="support@enginuo.com"
              />
            </CardContent>
          </Card>

          {showInvitationDescription && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <MessageSquare className="mr-2 h-5 w-5 text-primary-600" />
                Invitation description
              </CardTitle>
              <CardDescription>
                Completes the sentence: “You have been invited to join {brandName.trim() || 'your brand'}, …”
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                id="email_tagline"
                rows={3}
                maxLength={500}
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                required
              />
              <p className="mt-2 text-xs text-secondary-400">{tagline.trim().length}/500</p>
            </CardContent>
          </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <FileText className="mr-2 h-5 w-5 text-primary-600" />
                Footer
              </CardTitle>
              <CardDescription>Short company description shown at the bottom of emails.</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                id="email_footer"
                rows={4}
                maxLength={1000}
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                required
              />
              <p className="mt-2 text-xs text-secondary-400">{footerText.trim().length}/1000</p>
              <p className="mt-3 text-xs text-secondary-500">
                Emails also use the logo from the Appearance tab when one is uploaded.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="h-fit xl:sticky xl:top-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Mail className="mr-2 h-5 w-5 text-primary-600" />
                Email preview
              </CardTitle>
              <CardDescription>How {selectedTemplate.label.toLowerCase()} emails will look with this branding.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                aria-label="Preview email template"
              >
                {EMAIL_TEMPLATES.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.label}
                  </option>
                ))}
              </Select>
              <EmailPreview
                templateId={templateId}
                brandName={brandName.trim()}
                tagline={tagline.trim()}
                footerText={footerText.trim()}
                supportEmail={supportEmail.trim()}
                logoUrl={branding?.logo_url || ''}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      <div className={`fixed inset-x-0 bottom-0 z-20 border-t border-secondary-200 bg-white/95 backdrop-blur lg:left-64 ${active ? '' : 'hidden'}`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <p className="hidden text-sm text-secondary-500 sm:block">
            {isDirty ? 'Save to apply these brand settings to all Product Hub emails.' : 'Email branding is up to date.'}
          </p>
          <div className="ml-auto flex items-center gap-3">
            {isDirty && (
              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                Unsaved changes
              </span>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={applySavedBranding}
              disabled={mutation.isLoading || !isDirty}
            >
              Discard
            </Button>
            <Button type="submit" loading={mutation.isLoading} disabled={mutation.isLoading || !isDirty}>
              {!mutation.isLoading && <Check className="mr-2 h-4 w-4" />}
              Save email branding
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}
